import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories, movies } from '@/mocks/movies';

const sortOptions = [
  { value: 'default', label: '默认排序' },
  { value: 'rating', label: '评分最高' },
  { value: 'newest', label: '最新发布' },
  { value: 'popular', label: '最受欢迎' },
];

const categoryNameMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
  documentary: '纪录片',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('default');

  const activeSlug = slug || 'movie';
  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = categoryNameMap[activeSlug] || activeCategory?.name || '电影';

  const filteredMovies = useMemo(() => {
    let result = movies.filter((m) => m.category === activeSlug);

    switch (sortBy) {
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result = [...result].sort((a, b) => b.year - a.year);
        break;
      case 'popular':
        result = [...result].sort((a, b) => {
          if (a.isHot && !b.isHot) return -1;
          if (!a.isHot && b.isHot) return 1;
          return b.rating - a.rating;
        });
        break;
      default:
        break;
    }

    return result;
  }, [activeSlug, sortBy]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `成全影视 - ${categoryName}分类`,
    description: `成全影视${categoryName}分类，免费高清${categoryName}在线观看，海量${categoryName}资源免费免VIP观看。`,
    url: `https://bjtongshiji.cn/category/${activeSlug}`,
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: categoryName },
                ]}
              />

              <header className="mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  成全影视{categoryName}
                </h1>
                <p className="mt-2 text-sm text-foreground-500">
                  海量高清{categoryName}资源，免费在线观看，流畅不卡顿
                </p>
              </header>

              <div className="flex items-center gap-2 flex-wrap mb-4">
                {categories.filter((c) => c.slug !== 'all').map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-foreground-500">
                  共找到 <strong className="text-foreground-800">{filteredMovies.length}</strong> 部{categoryName}
                </p>
                <div className="flex items-center gap-1.5">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === opt.value
                          ? 'bg-secondary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的影视内容</p>
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}